import { SetState, GetState } from 'zustand';
import { LiliTransaction } from '../types/lili-transaction';
import { LinkedAccount } from '../hooks/useLinkedAccounts';

enum ETypesFilter {
  NONE = 'none',
  ATMS = 'ATM',
  PURCHASES = 'PURCHASE',
  CHECK_DEPOSITS = 'CHECK_DEPOSIT',
  FUND_TRANSFER = 'FUND_TRANSFER',
  REFUND = 'REFUND',
}

interface IFilter {
  accountNo: string;
  startDate: Date | null;
  endDate: Date | null;
  datePreset: string;
  recordCnt: number;
  page: number;
  pending: boolean;
  amountFrom: number;
  amountTo: number;
  transactionFilterType: ETypesFilter;
}

enum EContentTypes {
  DETAILS,
  CHOOSE_CATEGORY,
}

export enum TransactionTabs {
  BANK_TRANSACTIONS,
  LINKED_CREDIT_CARDS,
}

export interface ITransactionsSlice {
  transactionsList: LiliTransaction[];
  drawerContentType: EContentTypes | null;
  selectedTransaction: LiliTransaction | null;
  activeTransactionIndex: number | null;
  setTransactionsList: (data: LiliTransaction[]) => void;
  setSelectedTransaction: (data: LiliTransaction | null, category?: string, percent?: number) => void;
  setDrawerContentType: (contentType: EContentTypes | null) => void;
  filterObject: IFilter;
  setFilterObject: (filter: IFilter) => void;
  resetSlice: () => void;
  updateTransactionComment: (comment: string) => void;
  updateTransactionReceipt: (id: string) => void;
  updateTransactionCategory: (categoryId: string, businessPercent: number) => void;
  handleTransactionNavigation: (isUp: boolean) => void;
  selectedTransactionTab: TransactionTabs;
  setSelectedTransactionTab: (index: TransactionTabs) => void;
  selectedSyncedCard: LinkedAccount | null;
  setSelectedSyncedCard: (card: LinkedAccount | null) => void;
  showWarningCategoryModal: boolean;
  setShowWarningCategoryModal: (show: boolean) => void;
  showSyncCardModal: boolean;
  setShowSyncCardModal: (show: boolean) => void;
  resetTransactionsSlice: () => void;
}

const INITIAL_FILTER = {
  accountNo: '',
  startDate: null,
  endDate: null,
  datePreset: 'any',
  recordCnt: 50,
  page: 1,
  pending: false,
  amountFrom: 0,
  amountTo: 0,
  transactionFilterType: ETypesFilter.NONE,
};

const createTransactionSlice = (set: SetState<ITransactionsSlice>, get: GetState<ITransactionsSlice>) => ({
  transactionsList: [],
  selectedTransaction: null,
  activeTransactionIndex: null,
  filterObject: INITIAL_FILTER,
  drawerContentType: null,
  selectedTransactionTab: TransactionTabs.BANK_TRANSACTIONS,
  selectedSyncedCard: null,
  showWarningCategoryModal: false,
  showSyncCardModal: false,

  handleTransactionNavigation: (isUp: boolean) => {
    const activeIdx = get().activeTransactionIndex;
    const { transactionsList } = get();
    if (activeIdx === null) return;
    if (isUp && !activeIdx) return;
    if (!isUp && activeIdx === transactionsList.length - 1) return;
    let nextIdx: number;
    if (isUp) {
      nextIdx = activeIdx - 1;
    } else {
      nextIdx = activeIdx + 1;
    }
    set({ selectedTransaction: transactionsList[nextIdx], activeTransactionIndex: nextIdx });

    if (isUp && activeIdx <= 4) {
      const elWrapper = document.querySelector('.p-datatable-wrapper');
      if (elWrapper) elWrapper.scrollTo(0, 0);
    } else {
      const elRow = document.getElementById(transactionsList[nextIdx].transactionId);
      const elRowToScroll = document.getElementById(transactionsList[nextIdx - 3].transactionId);
      if (elRow) {
        const elRect = elRow.getBoundingClientRect();
        let isVisible: boolean;
        if (!isUp) {
          isVisible = elRect.top >= 0
            && elRect.left >= 0
            && elRect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
            && elRect.right <= (window.innerWidth || document.documentElement.clientWidth);
        } else {
          isVisible = elRect.top > 370;
        }

        if (!isVisible) {
          if (elRowToScroll) elRowToScroll.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
        }
      }
    }
  },

  setDrawerContentType: (contentType: EContentTypes | null) => {
    set({ drawerContentType: contentType });
  },
  setSelectedTransactionTab: (tab: TransactionTabs) => {
    set({ selectedTransactionTab: tab });
  },
  setSelectedSyncedCard: (card: LinkedAccount | null) => {
    set({ selectedSyncedCard: card });
  },
  setTransactionsList: (data: LiliTransaction[]) => {
    const shallowCopy = [...data];
    shallowCopy.forEach((t, idx) => {
      if (idx === data.length - 1) {
        t.isLast = true;
      } else {
        t.isLast = false;
      }
    });
    set({ transactionsList: shallowCopy });
  },
  setSelectedTransaction: async (data: LiliTransaction | null, category?: string, percent?: number) => {
    const list = get().transactionsList;
    const transactionIdx = list.findIndex((t) => t.transactionId === data?.transactionId);
    if (transactionIdx !== -1) {
      set({ activeTransactionIndex: transactionIdx });
    }
    set({ selectedTransaction: data });
    if (category && percent !== undefined) {
      get().updateTransactionCategory(category, percent);
    }
  },
  setShowWarningCategoryModal: (show: boolean) => {
    set({ showWarningCategoryModal: show });
  },
  setShowSyncCardModal: (show: boolean) => {
    set({ showSyncCardModal: show });
  },
  setFilterObject: (filter: IFilter) => {
    set({ filterObject: filter });
  },
  resetSlice: () => {
    set({
      transactionsList: [],
      selectedTransaction: null,
      activeTransactionIndex: null,
      filterObject: INITIAL_FILTER,
      drawerContentType: null,
    });
  },
  resetTransactionsSlice: () => {
    set({
      transactionsList: [],
      selectedTransaction: null,
      activeTransactionIndex: null,
      filterObject: INITIAL_FILTER,
      drawerContentType: null,
      selectedTransactionTab: TransactionTabs.BANK_TRANSACTIONS,
      selectedSyncedCard: null,
      showWarningCategoryModal: false,
      showSyncCardModal: false,
    });
  },
  updateTransactionComment: (comment: string): void => {
    const list = get().transactionsList;
    const listToUpdate = [...list];
    const selected = get().selectedTransaction;
    const transactionIdx = list.findIndex((t) => t.transactionId === selected?.transactionId);
    if (transactionIdx === -1) return;
    listToUpdate[transactionIdx].comment = comment;
    set({ transactionsList: listToUpdate });
  },
  updateTransactionReceipt: (id: string): void => {
    const list = get().transactionsList;
    const listToUpdate = [...list];
    const selected = get().selectedTransaction;
    const transactionIdx = list.findIndex((t) => t.transactionId === selected?.transactionId);
    if (transactionIdx === -1) return;
    if (!listToUpdate[transactionIdx].categoryDetails) {
      listToUpdate[transactionIdx].categoryDetails = {};
    }
    // @ts-ignore
    listToUpdate[transactionIdx].categoryDetails.receiptPath = id;
    set({ transactionsList: listToUpdate });
  },
  updateTransactionCategory: (categoryId: string, businessPercent: number): void => {
    const list = get().transactionsList;
    const listToUpdate = [...list];
    const selected = get().selectedTransaction;
    const transactionIdx = list.findIndex((t) => t.transactionId === selected?.transactionId);
    if (transactionIdx === -1) return;
    // @ts-ignore
    listToUpdate[transactionIdx].categoryDetails = {
      categoryExternalId: categoryId,
      businessPercent,
      business: businessPercent > 0,
    };
    set({ transactionsList: listToUpdate });
  },
});

export default createTransactionSlice;
